import { useMemo } from 'react';
import {
  type GetHostGameRequestsQuery,
  SportsEnum,
  useGetHostGameQuery,
  useGetHostGameRequestsQuery,
} from 'types/generated/client';
import { getVerificationLevel } from 'types/verification';
import getFirstNameLastInitial from 'utils/shared/string/getFirstNameLastInitial';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import { useViewer } from 'hooks/useViewer';
import type { FindGame } from 'screens/Find/types';
import { mapEventToFindGame } from 'screens/Find/useFindGames';
import { isUnlimitedOpenPlay } from 'components/Find/gameFormat';
import type { HostPlayRequest } from './types';

// Mirrors the accept route's check: the one case where it auto-declines the rest.
// Reads the roster, so callers must refetch GetHostGame after answering a request.
const isLastSeat = (game: FindGame): boolean => {
  if (isUnlimitedOpenPlay(game)) return false;
  const capacity = game.participantLimit ?? game.playersPerTeam * 2;
  const taken = game.teams.reduce((total, team) => total + team.length, 0);
  return taken + 1 >= capacity;
};

type HostRequestRow = GetHostGameRequestsQuery['playRequests'][number];

// Mirrors `buildHostPlayer`: skill comes from the requester's profile, never a default.
const mapRequest = (row: HostRequestRow, game: FindGame): HostPlayRequest => {
  const { sport } = game;
  const profile = row.requesterProfile;
  const displayName = profile?.preferredName || profile?.fullName || 'Player';
  const profileSkillLevel =
    sport === SportsEnum.Tennis
      ? profile?.tennisSkillLevel?.id
      : sport === SportsEnum.Pickleball
      ? profile?.pickleballSkillLevel?.id
      : undefined;

  return {
    id: row.id,
    variant: 'GAME',
    message: row.requestMessage,
    isLastSeat: isLastSeat(game),
    player: {
      name: getFirstNameLastInitial(displayName),
      username: profile?.username ?? undefined,
      imageUrl: getProfileImageUrlOrPlaceholder({ path: profile?.profileImagePath }),
      verificationLevel: getVerificationLevel({
        identityVerifications: profile?.identityVerifications,
      }),
      skillLevel: profileSkillLevel ?? undefined,
    },
  };
};

export function useHostGameDetail(eventId: string | null) {
  const { userId } = useViewer();
  const skip = !eventId || !userId;

  const { data: gameData, loading: gameLoading } = useGetHostGameQuery({
    variables: { eventId: eventId ?? '', viewerUserId: userId ?? '' },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const { data: requestsData, loading: requestsLoading } = useGetHostGameRequestsQuery({
    variables: { eventId: eventId ?? '', userId: userId ?? '' },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const game = useMemo<FindGame | null>(() => {
    const event = gameData?.events?.[0];
    return event ? mapEventToFindGame(event, null, null) : null;
  }, [gameData]);

  const requests = useMemo<HostPlayRequest[]>(() => {
    if (!game) return [];
    return (requestsData?.playRequests ?? []).map((row) => mapRequest(row, game));
  }, [requestsData, game]);

  return { game, requests, loading: gameLoading || requestsLoading };
}
